import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ExternalLink, ExternalSourceType } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save externalLink data.
 */
@Injectable()
export class ExternalLinkService {
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get a single externalLink's data
     */
    public get(eventId: string, externalLinkId: string, includes: string[] = null): Observable<ExternalLink> {
        const apiRequestUrl: string = this.getApiUrl(eventId, externalLinkId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new ExternalLink().deserialize(response))));
    }

    /**
     * Determine if the external identifier is valid
     */
    public isValid(
        eventId: string,
        externalSource: ExternalSourceType,
        externalIdentifier: string,
        eventDateTime?: Date,
    ): Observable<boolean> {
        const queryParams: string[] = [];

        if (eventDateTime) {
            queryParams.push(`eventStartDateTime=${eventDateTime.toISOString()}`);
        }

        const apiRequestUrl: string =
            this.getApiUrl(eventId, `is-valid/${externalSource}/${externalIdentifier}`, null, queryParams);

        return this.http.get<IIsValid>(apiRequestUrl).pipe(map((response: IIsValid) => response.isValid));
    }

    /**
     * Add or update the externalLink
     */
    public save(externalLink: ExternalLink, includes: string[] = null): Observable<ExternalLink> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(externalLink.eventId, externalLink.id, includes);

        if (externalLink.id) {
            // Update existing ExternalLink
            response = this.http.patch(apiRequestUrl, externalLink.serialize());
        } else {
            // Add a new ExternalLink
            response = this.http.post(apiRequestUrl, externalLink.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new ExternalLink()).deserialize(response)));
    }

    /**
     * Delete the specified externalLink.
     */
    public delete(eventId: string, externalLinkId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, externalLinkId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, externalLinkId: string, includes?: string[], queryParams?: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/external-links/`;

        if (externalLinkId) {
            url += externalLinkId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface IIsValid {
    isValid: boolean
}
